import { useEffect, useState } from 'react'
import { api, query } from '../api/client'
import { useQuery } from '../api/hooks'
import type {
  DocumentRow,
  DocumentSource,
  DocumentStatus,
  Paged,
  Party,
  PostableAccount,
  TaxDefaults,
} from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { P } from '../auth/permissions'
import { Attachments } from '../components/Attachments'
import {
  Empty,
  ErrorBanner,
  Field,
  Loading,
  Modal,
  Money,
  Panel,
  StatusTag,
  formatDate,
  isoDate,
} from '../components/ui'

/**
 * The document intake log (BUILD_PLAN §1 row 11, §10.5).
 *
 * A staging register, deliberately outside the ledger. This is the answer to "did we record that
 * invoice yet", and the main defence against a wrong VAT return. Nothing reaches the general
 * ledger until a row is posted.
 */
export function DocumentsPage() {
  const { can } = useAuth()
  const [status, setStatus] = useState<DocumentStatus | ''>('')
  const [source, setSource] = useState<DocumentSource | ''>('')
  const [logging, setLogging] = useState(false)
  const [posting, setPosting] = useState<DocumentRow | null>(null)
  const [filing, setFiling] = useState<DocumentRow | null>(null)

  const documents = useQuery<Paged<DocumentRow>>(
    `/documents${query({ status, source, size: 100 })}`,
    [status, source],
  )

  async function transition(document: DocumentRow, to: DocumentStatus) {
    let reason: string | null = null

    // A rejection without a reason tells the next person nothing, so the server requires one.
    if (to === 'Rejected') {
      reason = window.prompt('Why is this document being rejected?')
      if (!reason || !reason.trim()) return
    }

    await api(`/documents/${document.id}/transition`, { method: 'POST', body: { to, reason } })
    documents.reload()
  }

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value as DocumentStatus | '')}>
            <option value="">All</option>
            <option value="Logged">Logged</option>
            <option value="Reviewed">Reviewed</option>
            <option value="Approved">Approved</option>
            <option value="Posted">Posted</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>

        <div className="field">
          <label>Source</label>
          <select value={source} onChange={(e) => setSource(e.target.value as DocumentSource | '')}>
            <option value="">All</option>
            <option value="Portal">ETA portal</option>
            <option value="Issued">Issued by us</option>
            <option value="Manual">Manual</option>
            <option value="AlxImport">ALX import</option>
          </select>
        </div>

        <div className="spacer" />

        {can(P.documents.create) && (
          <button className="primary" onClick={() => setLogging(true)}>
            + Log a document
          </button>
        )}
      </div>

      <ErrorBanner error={documents.error} />
      {documents.loading && <Loading />}

      {documents.data && (
        <Panel flush>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Date</th>
                  <th style={{ width: 120 }}>Number</th>
                  <th style={{ width: 100 }}>Source</th>
                  <th>Party</th>
                  <th style={{ width: 110 }} className="num">
                    Net
                  </th>
                  <th style={{ width: 100 }} className="num">
                    VAT
                  </th>
                  <th style={{ width: 110 }} className="num">
                    Total
                  </th>
                  <th style={{ width: 90 }}>Status</th>
                  <th style={{ width: 210 }} />
                </tr>
              </thead>
              <tbody>
                {documents.data.items.length === 0 && (
                  <tr>
                    <td colSpan={9}>
                      <Empty>Nothing in the register for this filter.</Empty>
                    </td>
                  </tr>
                )}

                {documents.data.items.map((document) => (
                  <tr key={document.id}>
                    <td>{formatDate(document.date)}</td>
                    <td className="code">{document.docNo}</td>
                    <td className="soft">{document.source}</td>
                    <td>
                      {document.partyName ?? <span className="muted">unmatched</span>}
                      {document.rejectionReason && (
                        <div className="negative" style={{ fontSize: 11 }}>
                          {document.rejectionReason}
                        </div>
                      )}
                    </td>
                    <td className="num">
                      <Money value={document.amount} />
                    </td>
                    <td className="num">
                      <Money value={document.vatAmount} blankZero />
                    </td>
                    <td className="num strong">
                      <Money value={document.totalAmount} />
                    </td>
                    <td>
                      <StatusTag status={document.status} />
                    </td>
                    <td>
                      {/* The scan itself. Available at every status including Posted — the paper
                          arriving after the figures were keyed in is the normal order, not an
                          exception. */}
                      {can(P.documents.view) && (
                        <button className="subtle small" onClick={() => setFiling(document)}>
                          {document.attachmentCount > 0
                            ? `Files (${document.attachmentCount})`
                            : 'Attach'}
                        </button>
                      )}

                      {/* Logged → Reviewed → Approved → Posted, or Rejected with a reason. */}
                      {document.status === 'Logged' && can(P.documents.review) && (
                        <button className="subtle small" onClick={() => void transition(document, 'Reviewed')}>
                          Review
                        </button>
                      )}
                      {document.status === 'Reviewed' && can(P.documents.approve) && (
                        <button className="subtle small" onClick={() => void transition(document, 'Approved')}>
                          Approve
                        </button>
                      )}
                      {document.status === 'Approved' && can(P.documents.post) && (
                        <button className="subtle small" onClick={() => setPosting(document)}>
                          Post to ledger
                        </button>
                      )}
                      {document.status !== 'Posted' &&
                        document.status !== 'Rejected' &&
                        can(P.documents.review) && (
                          <button className="subtle small" onClick={() => void transition(document, 'Rejected')}>
                            Reject
                          </button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {logging && (
        <LogDocument
          onClose={() => setLogging(false)}
          onSaved={() => {
            setLogging(false)
            documents.reload()
          }}
        />
      )}

      {posting && (
        <PostDocument
          document={posting}
          onClose={() => setPosting(null)}
          onSaved={() => {
            setPosting(null)
            documents.reload()
          }}
        />
      )}

      {filing && (
        <Modal
          title={`${filing.docNo} — files`}
          onClose={() => {
            setFiling(null)
            // The count on the row came with the list, so it is stale the moment anything is
            // uploaded or removed in here.
            documents.reload()
          }}
        >
          <Attachments
            entityType="DocumentIntake"
            entityId={filing.id}
            hint="The scan of the invoice or receipt. PDFs, images and Office documents, up to 20 MB each."
          />
        </Modal>
      )}
    </>
  )
}

function LogDocument({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const parties = useQuery<Party[]>('/parties')
  const taxDefaults = useQuery<TaxDefaults>('/tax-defaults')

  const [form, setForm] = useState({
    source: 'Portal' as DocumentSource,
    docNo: '',
    date: isoDate(new Date()),
    partyId: '',
    vatRegistrationNo: '',
    amount: '',
    vatAmount: '',
    withholdingAmount: '',
    notes: '',
  })

  // Which tax boxes the person has typed in themselves. The defaults fill an untouched box and
  // stop the moment somebody disagrees with them — a document that says 5% VAT must not have it
  // silently restored to 14% by the next keystroke in the net amount.
  const [typed, setTyped] = useState({ vatAmount: false, withholdingAmount: false })

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((previous) => ({ ...previous, [key]: value }))
  }

  /** A rate typed as a figure, to two places. Blank in, blank out — not a zero. */
  function at(rate: number | undefined, amount: string): string {
    const net = Number(amount)
    if (!rate || !amount.trim() || Number.isNaN(net)) return ''
    return (Math.round(net * rate * 100) / 100).toFixed(2)
  }

  /**
   * The net amount and the source both drive the tax boxes, so they are applied together.
   *
   * Withholding is deducted from what we pay a supplier, so it is pre-filled on a document that
   * came to us and left blank on one we issued — which is what the setting itself says it is for.
   * Changing the source back and forth re-decides it, until somebody types in the box.
   */
  function apply(next: { amount?: string; source?: DocumentSource }) {
    setForm((previous) => {
      const amount = next.amount ?? previous.amount
      const source = next.source ?? previous.source

      return {
        ...previous,
        amount,
        source,
        vatAmount: typed.vatAmount ? previous.vatAmount : at(taxDefaults.data?.vatRate, amount),
        withholdingAmount: typed.withholdingAmount
          ? previous.withholdingAmount
          : source === 'Issued'
            ? ''
            : at(taxDefaults.data?.whtRate, amount),
      }
    })
  }

  function setTax(key: 'vatAmount' | 'withholdingAmount', value: string) {
    // Emptying the box hands it back to the default instead of pinning it empty for good. Clearing
    // a figure to start again is not the same as disagreeing with the rate, and a box that never
    // fills again is indistinguishable from the bug this replaced.
    setTyped((previous) => ({ ...previous, [key]: value.trim() !== '' }))
    set(key, value)
  }

  /**
   * The rates come from the server, so on a slow response the amount can be typed before they
   * land — and `at()` fills nothing without a rate. Re-derive the untouched boxes once they
   * arrive; a box somebody has already typed in is left exactly as they left it.
   */
  useEffect(() => {
    if (!taxDefaults.data) return
    apply({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxDefaults.data])

  /** 0.14 as "14%". Rounded first, or 0.14 × 100 prints as 14.000000000000002. */
  const percent = (rate: number | undefined) =>
    rate === undefined ? '' : `${Math.round(rate * 10000) / 100}%`

  async function save() {
    setBusy(true)
    setError(undefined)

    try {
      await api('/documents', {
        method: 'POST',
        body: {
          source: form.source,
          docNo: form.docNo.trim(),
          date: form.date,
          partyId: form.partyId || null,
          vatRegistrationNo: form.vatRegistrationNo || null,
          currency: 'EGP',
          amount: Number(form.amount) || 0,
          vatAmount: Number(form.vatAmount) || 0,
          withholdingAmount: Number(form.withholdingAmount) || 0,
          notes: form.notes || null,
        },
      })
      onSaved()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title="Log a document"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={busy || !form.docNo} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Log it'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} onDismiss={() => setError(undefined)} />

      <div className="banner info">
        Logging a document does not touch the ledger. It enters the register at status{' '}
        <strong>Logged</strong> and only reaches the general ledger once it has been reviewed,
        approved and posted.
      </div>

      <div className="grid cols-2">
        <Field label="Source">
          <select
            value={form.source}
            onChange={(e) => apply({ source: e.target.value as DocumentSource })}
          >
            <option value="Portal">ETA portal — purchase invoice</option>
            <option value="Issued">Issued by us — fee statement</option>
            <option value="Manual">Manual — paper document</option>
          </select>
        </Field>

        <Field label="Document number">
          <input value={form.docNo} onChange={(e) => set('docNo', e.target.value)} />
        </Field>

        <Field label="Document date">
          <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} />
        </Field>

        <Field label="Party" hint="Leave blank to match later by VAT registration.">
          <select value={form.partyId} onChange={(e) => set('partyId', e.target.value)}>
            <option value="">Unmatched</option>
            {parties.data?.map((party) => (
              <option key={party.id} value={party.id}>
                {party.code} · {party.nameEn}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Net amount">
          <input
            className="num"
            inputMode="decimal"
            value={form.amount}
            onChange={(e) => apply({ amount: e.target.value })}
          />
        </Field>

        <Field
          label="VAT"
          hint={
            // Only when there is a rate to name. A default of zero fills nothing, so saying so
            // would describe a box that behaves exactly as it did before.
            taxDefaults.data?.vatRate
              ? `Filled at ${percent(taxDefaults.data.vatRate)} of the net amount. Type over it if the document says otherwise.`
              : undefined
          }
        >
          <input
            className="num"
            inputMode="decimal"
            value={form.vatAmount}
            onChange={(e) => setTax('vatAmount', e.target.value)}
          />
        </Field>

        <Field
          label="Withholding tax"
          hint={
            !taxDefaults.data?.whtRate
              ? undefined
              : form.source === 'Issued'
                ? 'Deducted by the client from what they pay us, so it is left blank here.'
                : `Filled at ${percent(taxDefaults.data.whtRate)} — deducted from what we pay the supplier, not from the expense.`
          }
        >
          <input
            className="num"
            inputMode="decimal"
            value={form.withholdingAmount}
            onChange={(e) => setTax('withholdingAmount', e.target.value)}
          />
        </Field>
      </div>

      <Field label="Notes">
        <input value={form.notes} onChange={(e) => set('notes', e.target.value)} />
      </Field>
    </Modal>
  )
}

function PostDocument({
  document,
  onClose,
  onSaved,
}: {
  document: DocumentRow
  onClose: () => void
  onSaved: () => void
}) {
  const accounts = useQuery<PostableAccount[]>('/accounts/postable')
  const [accountId, setAccountId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()

  const wantsRevenue = document.source === 'Issued'

  const candidates = (accounts.data ?? []).filter((a) =>
    wantsRevenue ? a.type === 'Revenue' : a.type === 'Expense',
  )

  async function submit() {
    setBusy(true)
    setError(undefined)

    try {
      await api(`/documents/${document.id}/post`, { method: 'POST', body: { accountId } })
      onSaved()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title={`Post ${document.docNo} to the ledger`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={busy || !accountId} onClick={() => void submit()}>
            {busy ? 'Posting…' : 'Post'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} onDismiss={() => setError(undefined)} />

      <div className="banner info">
        Recognizing an invoice is <strong>accrual only</strong>. The cash column picks it up when
        the money actually moves.
      </div>

      <Field
        label={wantsRevenue ? 'Revenue account' : 'Expense account'}
        hint="VAT and the party sub-ledger line are added automatically."
      >
        <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
          <option value="">Choose an account…</option>
          {candidates.map((account) => (
            <option key={account.id} value={account.id}>
              {account.code} · {account.nameEn}
            </option>
          ))}
        </select>
      </Field>
    </Modal>
  )
}
